<template>
  <div class="container-fluid pt-3">
    <h4 class="text-center py-3">
      <span @click="decWeek" class="align-middle"><i class="fas fa-chevron-left"></i></span>

      <span class="btn shadow btn-primary mx-4 rounded-pill">
        {{formatDate(slider.from)}} - {{formatDate(slider.to)}}
      </span>

      <span @click="incWeek" class="align-middle"><i class="fas fa-chevron-right"></i></span>
    </h4>

    <div class="text-center mb-3">
      <button v-for="(day, dayKey) in slider.days" :key="dayKey"
              type="button "
              :class="'btn btn py-2 rounded-circle ' + (isCurrenctDay(day) ? 'btn-primary shadow' : 'btn-link text-secondary')"
              @click="selectDay(day)">{{days[day.getDay()]}}
      </button>
    </div>

    <div v-for="(time, timeKey) in data" :key="timeKey"
         :class="'card shadow mb-3 ' + (time.methods.length === 0 ? 'opacity-50' : '')">
      <div :class="'card-header font-weight-bold ' + (time.methods.length !== 0 ? 'text-secondary' : 'text-muted')">
        {{ time.name }}
      </div>

      <ul class="list-group list-group-flush" v-for="(method, methodKey) in time.methods" :key="methodKey">
        <li class="list-group-item font-weight-bold text-secondary bg-light">
          {{ method.name }}
        </li>

        <li class="list-group-item pointer position-relative"
            :style="'padding-left:40px;padding-right:' + (item.startedAt === null ? '75px': '0')"
            v-for="(item, itemKey) in method.items"
            :key="itemKey">
          <div @click="showDetails(item.id + time.id)"
               class="position-absolute text-center pt-4"
               style="width:40px;left:0;top:0;">
            <i :class="'fas text-secondary fa-angle-' + (details === item.id + time.id ? 'up' : 'down')"></i>
          </div>

          <div @click="showDetails(item.id + time.id)">
            <div>
              {{item.drug.name}}

              <small class="ml-1">
                {{ item.drug.measure }}
                {{ item.drug.measureUnit }}
              </small>
            </div>

            <small class="text-muted d-block">
              {{item.quantity}}
              {{ declensionNumbers(item.quantity, item.drug.format.name) }}
            </small>
          </div>

          <div v-show="details === item.id + time.id">
            <div class="">
              <small>{{ item.comment }}</small>
            </div>

            <div v-if="item.startedAt !== null">
              <small class="text-secondary text-uppercase" v-if="durationLeft(item) > 0">
                Осталось
                <span class="font-weight-bold">{{ durationLeft(item) }}</span>
                {{declensionNumbers(durationLeft(item), 'день')}}
              </small>
              <small class="text-secondary text-uppercase" v-else-if="durationLeft(item) === 0">
                Последний день приема
              </small>
              <small class="text-secondary text-uppercase" v-else>
                Прием окончен
              </small>
            </div>

            <div v-else class="card-body__days">
              <small class="text-danger text-uppercase">Нужно начать прием.</small>
            </div>

            <div>
              <small>{{ item.drug.description }}</small>
            </div>
          </div>
        </li>
      </ul>

      <div v-if="time.methods.length === 0">
        <div class="text-muted m-2">
          <small>Назначени на это время нет</small>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import calculateDaysCount from '../../../utils/calculateDaysCount';
  import formatDate from '../../../utils/formatDate';
  import declensionNumbers from '../../../utils/declensionNumbers';
  import {MS_IN_DAY, FORMAT_NAMES} from '../../../constants';

  export default {
    name: 'DrugSchedule',

    props: ['drugPrescriptions'],

    data() {
      return {
        details: '',
        date: new Date(),
        days: [
          'Вс',
          'Пн',
          'Вт',
          'Ср',
          'Чт',
          'Пт',
          'Сб',
        ],
      };
    },

    computed: {
      slider() {
        const slider = { from: new Date(this.date.getTime()), to: new Date(this.date.getTime()), days: [] };
        const shift = this.date.getDay() === 0 ? 6 : this.date.getDay() - 1;

        slider.from.setTime(slider.from.getTime() - shift * MS_IN_DAY);
        slider.to.setTime(slider.from.getTime());
        slider.to.setTime(slider.from.getTime() + 6 * MS_IN_DAY);

        for (let i = slider.from.getTime(), it = slider.to.getTime(); i <= it; i += MS_IN_DAY) {
          slider.days.push(new Date(i));
        }

        return slider;
      },

      data() {
        const data = this.$store.state.times.map(({id, name, sort}) => {
          return {id, name, sort, methods: []};
        });
        const currentPrescriptions = this.drugPrescriptions.filter(prescription => {
          const startDate = new Date(prescription.startDate);
          const finishDate = new Date(prescription.finishDate);

          if (prescription.startedAt !== null) {
            const start = new Date(prescription.startedAt);
            const daysPrescribed = calculateDaysCount(prescription.startDate, prescription.finishDate) - 1;

            finishDate.setTime(start.getTime() + daysPrescribed * MS_IN_DAY);
          }

          const from = formatDate(startDate, 'ymd');
          const to = formatDate(finishDate, 'ymd');
          const day = formatDate(this.date, 'ymd');

          return from <= day && day <= to;
        });

        currentPrescriptions.forEach(prescription => {
          prescription.times.forEach(prescriptionTime => {
            data.forEach(dataTime => {
              if (prescriptionTime.id !== dataTime.id) {
                return;
              }

              prescription.methods.forEach(prescriptionMethod => {
                const method = dataTime.methods.find(dataMethod => dataMethod.id === prescriptionMethod.id);

                if (method) {
                  return method.items.push(prescription);
                }

                dataTime.methods.push({
                  id: prescriptionMethod.id,
                  name: prescriptionMethod.name,
                  sort: prescriptionMethod.sort,
                  items: [prescription],
                });
              });
            });
          });
        });

        data.sort((a, b) => a.sort - b.sort);
        data.forEach(time => time.methods.sort((a, b) => a.sort - b.sort));

        return data;
      },
    },

    methods: {
      showDetails(id) {
        this.details = this.details === id ? '' : id;
      },

      isCurrenctDay(date) {
        return this.date.getDay() === date.getDay();
      },

      incWeek() {
        this.date = new Date(this.date.getTime() + 7 * MS_IN_DAY);
      },

      decWeek() {
        this.date = new Date(this.date.getTime() - 7 * MS_IN_DAY);
      },

      selectDay(date) {
        this.date = date;
      },

      durationLeft(prescription) {
        if (prescription.startedAt === null) {
          return 0;
        }

        const daysPrescribed = calculateDaysCount(prescription.startDate, prescription.finishDate);
        const daysInProgress = calculateDaysCount(prescription.startedAt, this.date.getTime());

        return Math.ceil(daysPrescribed - daysInProgress);
      },

      formatDate(date) {
        return formatDate(date, 'd.m.y');
      },

      declensionNumbers(count, name) {
        const formatName = FORMAT_NAMES.find(item => item[0] === name);
        const [, few, one, two ] = formatName || [name, name, name, name];

        return declensionNumbers(count, few, one, two);
      },
    }
  };
</script>
